(ns practitest.frontend.components.datepicker2
  "Pure CLJS implementation for datepicker with MouseUX design"
  (:require [clojure.string :as str]
            [practitest.frontend.common.dom :as dom]
            [practitest.frontend.common.format-utils :as fu]
            [practitest.frontend.components2.buttons :as buttons]
            [reagent.core :as reagent]
            [tick.core :as tick]
            [cljc.java-time.local-date]
            [cljc.java-time.day-of-week]
            [practitest.frontend.components.icons :as icons]
            [practitest.frontend.common.i18n :refer [get-translation]]
            [practitest.frontend.components.overlay2 :as overlay2]))

(defn- prev-day [date]
  (cljc.java-time.local-date/minus-days date 1))

(defn- next-day [date]
  (cljc.java-time.local-date/plus-days date 1))

(defn- prev-week [date]
  (cljc.java-time.local-date/minus-weeks date 1))

(defn- next-week [date]
  (cljc.java-time.local-date/plus-weeks date 1))

(defn- prev-month [date]
  (cljc.java-time.local-date/minus-months date 1))

(defn- next-month [date]
  (cljc.java-time.local-date/plus-months date 1))

(def ^:private days-in-week 7)
(def ^:private five-rows-cells (* days-in-week 5))
(def ^:private six-rows-cells (* days-in-week 6))


;; Generates month layout
(defn generate-month-layout
  [date week-start-day]
  (let [date (tick/date date)

        first-day (tick/first-day-of-month date)
        total-days (cljc.java-time.local-date/length-of-month date)

        prev-month (prev-month date)
        total-days-prev-month (cljc.java-time.local-date/length-of-month prev-month)
        ;; Calculate amount of days from previous month that should be displayed
        leading-days-prev-month (mod (- (cljc.java-time.day-of-week/ordinal (tick/day-of-week first-day))
                                        week-start-day)
                                     days-in-week)
        prev-month-days (range (inc (- total-days-prev-month leading-days-prev-month))
                               (inc total-days-prev-month))

        ;; It's either 5 rows - then it's 35 cells or 6 rows - then it's 42 cells
        total-cells (if (<= (+ total-days leading-days-prev-month) five-rows-cells)
                      five-rows-cells
                      six-rows-cells)

        next-month (next-month date)
        next-month-days (range 1 (inc (- total-cells total-days leading-days-prev-month)))
        ]
    ;; TODO: just tick dates ?

    (concat
      ;; Previous month
      (map (fn [day]
             {:day  day
              :date (tick/new-date (tick/year prev-month) (tick/month prev-month) day)})
           prev-month-days)
      ;; Current month
      (map (fn [day]
             {:day  day
              :date (tick/new-date (tick/year date) (tick/month date) day)})
           (range 1 (inc total-days)))
      ;; Next month
      (map (fn [day]
             {:day  day
              :date (tick/new-date (tick/year next-month) (tick/month next-month) day)})
           next-month-days))))

(def months
  [tick/JANUARY tick/FEBRUARY tick/MARCH tick/APRIL
   tick/MAY tick/JUNE tick/JULY tick/AUGUST
   tick/SEPTEMBER tick/OCTOBER tick/NOVEMBER tick/DECEMBER])

(defn- translate-dow
  [dow]
  (let [string (str dow)]
    ;; e.g. datepicker.day-of-week.SUNDAY
    (get-translation (str "datepicker.day-of-week." string)
                     ;; By default - 1st letter of day
                     (subs string 0 1))))

(defn- translate-month
  [month full?]
  (let [string (str month)]
    (if full?
      ;; Full version of month name
      (get-translation (str "datepicker.month." string)
                       (str/capitalize string))

      ;; Short version - used in month picker
      (get-translation (str "datepicker.month-short." string)
                       ;; By default - 1st 3 letters of month, capitalized
                       (str/capitalize (subs string 0 3))))))

;; inline version - works with dates only
(defn date-picker-inline [props]
  (let [local-state (reagent/atom {:expanded? false})]
    (fn
      [{:keys [on-change
               value

               shown-date
               on-change-shown-date

               on-apply
               on-cancel]
        :as   props}]
      (let [today (tick/date)
            value* (or shown-date
                       ;; expected to be tick object
                       value
                       today)

            layout (generate-month-layout value*
                                          ;; TODO: detect week start date from user locale, using Monday here
                                          0)

            expanded? (:expanded? @local-state)
            shown-month (tick/month value*)
            shown-year (tick/year value*)]
        [:div.datepicker-container
         [:div.datepicker-controls
          [:span.datepicker-shown-month
           (translate-month shown-month true)]

          [:span.datepicker-shown-year
           (str shown-year)]

          [:span.datepicker-button.datepicker-selector-chevron
           {:on-click (fn [e]
                        (swap! local-state update :expanded? not))}
           (if expanded?
             [icons/mux-chevron-up2]
             [icons/mux-chevron-down2])]

          [:div.datepicker-buttons
           [:span.datepicker-button.datepicker-prev-month
            {:on-click (fn [e]
                         (when on-change-shown-date
                           (on-change-shown-date (prev-month value*))))}
            [icons/mux-chevron-left]]
           [:span.datepicker-button.datepicker-today
            {:on-click (fn [e]
                         (when on-change-shown-date
                           (on-change-shown-date (tick/date))))}
            [icons/mux-current-location]]
           [:span.datepicker-button.datepicker-next-month
            {:on-click (fn [e]
                         (when on-change-shown-date
                           (on-change-shown-date (next-month value*))))}
            [icons/mux-chevron-right]]]]

         [:hr.datepicker-separator]

         (if expanded?
           [:div.datepicker-expanded
            (let [prev-year-date (cljc.java-time.local-date/minus-years value* 1)
                  next-year-date (cljc.java-time.local-date/plus-years value* 1)]
              [:div.datepicker-year-selector
               [:div.datepicker-button.datepicker-set-prev-year
                {:on-click (fn [e]
                             (when on-change-shown-date
                               (on-change-shown-date prev-year-date)))}
                [icons/mux-chevron-up2]]

               [:div.datepicker-button.datepicker-prev-year
                {:on-click (fn [e]
                             (when on-change-shown-date
                               (on-change-shown-date prev-year-date)))}
                (str (tick/year prev-year-date))]

               [:div.datepicker-button.datepicker-current-year
                (str shown-year)]

               [:div.datepicker-button.datepicker-next-year
                {:on-click (fn [e]
                             (when on-change-shown-date
                               (on-change-shown-date next-year-date)))}
                (str (tick/year next-year-date))]

               [:div.datepicker-button.datepicker-set-next-year
                {:on-click (fn [e]
                             (when on-change-shown-date
                               (on-change-shown-date next-year-date)))}
                [icons/mux-chevron-down2]]])

            [:hr.datepicker-separator]

            ;; Month selection
            (into [:div.datepicker-month-selector]
                  (map (fn [month]
                         [:div.month
                          {:key      (str "month-" (str month))
                           :class    (dom/classes-pairs-v
                                       :today (= (tick/month today) month)
                                       :selected (when value
                                                   (= (tick/month value) month)))
                           :on-click (fn [e]
                                       (swap! local-state assoc
                                              :expanded? false)
                                       (when on-change-shown-date
                                         (on-change-shown-date (cljc.java-time.local-date/with-month
                                                                 value*
                                                                 month))))}
                          (translate-month month false)]))
                  months)]

           [:div.calendar-grid
            ;; Header
            (into [:<>]
                  (map (fn [day]
                         (let [dow (tick/day-of-week (:date day))]
                           [:div.weekday-name
                            {:key (str "weekday-" (str dow))}
                            ;; Will handle Sun/Mon starting day
                            (translate-dow dow)])))
                  (take 7 layout))

            (into [:<>]
                  (map
                    (fn [day]
                      (let [date (:date day)
                            month-number (cljc.java-time.local-date/get-month-value date)
                            month-day (cljc.java-time.local-date/get-day-of-month date)]
                        [:div.day-cell
                         {:key      (str "day-" month-number "-" month-day)
                          :on-click (fn [e]
                                      (swap! local-state assoc :selected-date date)
                                      (when on-change
                                        ;; trigger on-change event
                                        (on-change date)))
                          :class    (dom/classes-pairs-v
                                      :different-month (not= shown-month (tick/month date))
                                      :today (= today date)
                                      :selected (when value
                                                  (= (tick/date value) date)))}
                         (fu/format-integer (:day day) 2)])))

                  ;; CSS grid will handle positioning
                  layout)])

         [:hr.datepicker-separator]

         ;; Apply/Cancel buttons
         [:div.datepicker-form-buttons
          ;; TODO: this cancel button behaviour is confusing, not sure how to combine it with input editing
          [buttons/button
           {:on-click on-cancel
            :text (get-translation "generic.button.cancel" "Cancel")}]
          [buttons/button
           {:on-click on-apply
            :primary? true
            :text (get-translation "generic.button.apply" "Apply")}]]
         ]))))

(def local-date-formatter (tick/formatter "dd-MMM-yyyy"))
(def local-date-time-formatter (tick/formatter "dd-MMM-yyyy HH:mm"))

(defn- try-parse-local-date
  [s]
  (when (string? s)
    (try
      (tick/parse-date s local-date-formatter)
      (catch js/Error e
        nil))))

(defn- try-parse-local-date-time
  [s]
  (when (string? s)
    (try
      (tick/parse-date-time s local-date-time-formatter)
      (catch js/Error e
        nil))))

(defn- format-date-object
  [date formatter]
  (when date
    (tick/format formatter date)))


(defn date-picker-input
  [_]
  (let [local-state (reagent/atom {:show?         false
                                   :editing-value nil
                                   :shown-date    nil})
        input-ref (atom nil)]
    (fn [{:keys [on-focus
                 on-blur
                 on-change
                 on-apply
                 value
                 class
                 time?
                 id]
          :as props}]
      (let [
            ;; intermittent value when editing
            editing-value (:editing-value @local-state)
            formatter (if time?
                        local-date-time-formatter
                        local-date-formatter)

            parser-fn (fn [val]
                        (if time?
                          (try-parse-local-date-time val)
                          (try-parse-local-date val)))

            value (when value
                    (if (string? value)
                      (try
                        (if time?
                          (tick/zoned-date-time (tick/instant value))
                          (tick/date value))
                        (catch js/Error e
                          nil))
                      value))

            value (when value
                    (if time?
                      ;; Uses default browser timezone
                      (if (tick/date? value)
                        (tick/zoned-date-time (tick/at value (tick/time (tick/now))))
                        (tick/zoned-date-time (tick/instant value)))
                      value))

            show? (:show? @local-state)

            key-handler-fn (fn [e]
                             (let [key (.-key e)
                                   value (or value (tick/date))]
                               (cond
                                 (= "ArrowDown" key)
                                 (do
                                   (dom/prevent-default e)
                                   (on-change (next-week value)))

                                 (= "ArrowUp" key)
                                 (do
                                   (dom/prevent-default e)
                                   (on-change (prev-week value)))

                                 (= "ArrowRight" key)
                                 (do
                                   (dom/prevent-default e)
                                   (on-change (next-day value)))

                                 (= "ArrowLeft" key)
                                 (do
                                   (dom/prevent-default e)
                                   (on-change (prev-day value)))

                                 (= "Enter" key)
                                 (do
                                   (dom/prevent-default e)
                                   (dom/stop-propagation e)
                                   (swap! local-state assoc :initial-value (:value props))
                                   (swap! local-state update :show? not))

                                 (= "Escape" key)
                                 (do
                                   (dom/prevent-default e)
                                   (dom/stop-propagation e)
                                   (when show?
                                     (on-change (:initial-value @local-state))
                                     (swap! local-state assoc :show? false))))))]

        [overlay2/floating-ui-overlay
         {:placement        "bottom-start"
          :show?            show?
          :flip             true
          :on-request-close #(swap! local-state assoc :show? false)}
         ;; Target
         [:div.datepicker-input-holder
          [:input
           {:class     class
            :id        id
            :ref       #(reset! input-ref %)
            :on-focus  (fn [e]
                         (when on-focus
                           (on-focus e)))

            :value     (or editing-value
                           (format-date-object value formatter))

            :on-key-down key-handler-fn

            :on-change (fn [e]
                         (let [val (dom/event-value e)]
                           (swap! local-state assoc :editing-value val)

                           (when-let [parsed (parser-fn val)]
                             (swap! local-state assoc
                                    :shown-date parsed
                                    :editing-value nil
                                    :input-error? false)
                             (on-change parsed))

                           ;; Special case for empty string - erase our value
                           (when (str/blank? val)
                             (on-change nil))))

            :on-blur   (fn [e]
                         (when (nil? (parser-fn (:editing-value @local-state)))
                           ;; TODO: show error notification
                           (swap! local-state dissoc :editing-value))

                         (when on-blur
                           (on-blur e)))

            ;; TODO: reuse formatter somehow ?
            :placeholder (if time?
                           "DD-MMM-YYYY HH:MM"
                           "DD-MMM-YYYY")}]

          [:span.datepicker-input-icon
           {:on-click (fn [e]
                        ;; store initial value
                        (swap! local-state assoc :initial-value (:value props))
                        (swap! local-state update :show? not)

                        (if (:show? @local-state)
                          (when on-focus
                            (on-focus e))
                          (when on-blur
                            (on-blur e))))}
           [icons/mux-calendar-manual]]]
         ;; Overlay
         [:div.dropdown-overlay
          (let [on-change* (fn [new-value]
                             (when on-change
                               ;; new-value is local-date here
                               (on-change (if time?
                                            ;; Use selected time and new date
                                            (tick/at (tick/date new-value)
                                                     (tick/time (or value (tick/now))))
                                            new-value))))]
            [date-picker-inline
             {:shown-date           (:shown-date @local-state)
              :on-change-shown-date (fn [date]
                                      (swap! local-state assoc :shown-date date))

              :on-cancel            (fn [e]
                                      ;; TODO: cleanup form state ? (will be marked :dirty?)
                                      (on-change (:initial-value @local-state))
                                      (swap! local-state assoc :show? false)
                                      (when on-blur
                                        (on-blur e)))
              :on-apply             (fn [e]
                                      ;; we already have latest value, just reapply it and close popup
                                      (on-change* value)
                                      (swap! local-state assoc :show? false)
                                      (when on-apply
                                        (on-apply value))
                                      (when on-focus
                                        (on-focus e)))

              :value                value
              :on-change            (fn [new-value]
                                      ;; focus back to input after date is selected
                                      (when-let [node @input-ref]
                                        (.focus node #js {:preventScroll true}))

                                      (on-change* new-value))}])]]))))
